import React, { useState } from 'react';

export default function AppointmentPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    insuranceProvider: '',
    physicianName: '',
    additionalNotes: '',
    consent: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const services = [
    "DOT Specimen Collection",
    "Non-DOT Specimen Collection",
    "DNA Testing",
    "Blood Testing",
    "Urine Drug Testing",
    "Flu Testing",
    "Electronic Fingerprint",
    "I-9 Background Check",
    "C/TPA Services"
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = "Invalid phone format (e.g., 984-230-9599)";
    }
    if (!formData.dob) newErrors.dob = "Date of birth is required";
    if (!formData.serviceType) newErrors.serviceType = "Please select a service";
    if (!formData.preferredDate) newErrors.preferredDate = "Preferred date is required";
    if (!formData.preferredTime) newErrors.preferredTime = "Preferred time is required";
    if (!formData.consent) newErrors.consent = "You must agree to the terms";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate form submission (replace with actual API call)
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dob: '',
        serviceType: '',
        preferredDate: '',
        preferredTime: '',
        insuranceProvider: '',
        physicianName: '',
        additionalNotes: '',
        consent: false
      });
    }, 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-[#4AB8D8] hover:text-[#F8A356]">Home</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">Book Appointment</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Book an Appointment</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Schedule your laboratory testing appointment online - fast, secure, and convenient
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {submitSuccess && (
          <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8 rounded-lg">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-green-800">Appointment Request Submitted!</h3>
                <p className="text-green-700 mt-1">We'll contact you within 24 hours to confirm your appointment. Check your email for confirmation details.</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#4AB8D8]">Personal Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.firstName}
                  />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.lastName}
                  />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="984-230-9599"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.phone}
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label htmlFor="dob" className="block text-sm font-semibold text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dob ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.dob}
                  />
                  {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob}</p>}
                </div>
              </div>
            </div>

            {/* Appointment Details */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#4AB8D8]">Appointment Details</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="serviceType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.serviceType ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.serviceType}
                  >
                    <option value="">-- Select a Service --</option>
                    {services.map((service, idx) => (
                      <option key={idx} value={service}>{service}</option>
                    ))}
                  </select>
                  {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>}
                </div>
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.preferredDate ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.preferredDate}
                  />
                  {errors.preferredDate && <p className="text-red-500 text-sm mt-1">{errors.preferredDate}</p>}
                </div>
                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Time <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.preferredTime ? 'border-red-500' : 'border-gray-300'}`}
                    aria-required="true"
                    aria-invalid={!!errors.preferredTime}
                  >
                    <option value="">-- Select Time --</option>
                    <option value="08:00 AM">08:00 AM</option>
                    <option value="09:00 AM">09:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="01:00 PM">01:00 PM</option>
                    <option value="02:00 PM">02:00 PM</option>
                    <option value="03:00 PM">03:00 PM</option>
                    <option value="04:00 PM">04:00 PM</option>
                    <option value="05:00 PM">05:00 PM</option>
                  </select>
                  {errors.preferredTime && <p className="text-red-500 text-sm mt-1">{errors.preferredTime}</p>}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-[#4AB8D8]">Additional Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="insuranceProvider" className="block text-sm font-semibold text-gray-700 mb-2">
                    Insurance Provider (Optional)
                  </label>
                  <input
                    type="text"
                    id="insuranceProvider"
                    name="insuranceProvider"
                    value={formData.insuranceProvider}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="physicianName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Referring Physician (Optional)
                  </label>
                  <input
                    type="text"
                    id="physicianName"
                    name="physicianName"
                    value={formData.physicianName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="additionalNotes" className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Notes or Questions
                  </label>
                  <textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Any special requirements, questions, or information we should know..."
                  />
                </div>
              </div>
            </div>

            {/* Consent */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="consent"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 text-[#4AB8D8] border-gray-300 rounded focus:ring-2 focus:ring-[#4AB8D8]"
                  aria-required="true"
                  aria-invalid={!!errors.consent}
                />
                <label htmlFor="consent" className="text-sm text-gray-700">
                  <span className="font-semibold">I agree</span> to the terms and conditions, privacy policy, and consent to receive appointment reminders via email and SMS. I understand that this is an appointment request and will be confirmed by EmergeNC Diagnostics staff. <span className="text-red-500">*</span>
                </label>
              </div>
              {errors.consent && <p className="text-red-500 text-sm mt-2 ml-8">{errors.consent}</p>}
            </div>

            {/* CAPTCHA Placeholder */}
            <div className="bg-[#4AB8D8]/10 border-2 border-[#4AB8D8]/30 rounded-lg p-6 text-center">
              <p className="text-gray-700 font-semibold mb-2">🔒 Security Verification</p>
              <p className="text-sm text-gray-600">reCAPTCHA verification will be enabled in production</p>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#4AB8D8] text-white px-8 py-4 rounded-lg hover:bg-[#3A9AB8] transition-colors font-semibold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Appointment Request'}
              </button>
              <button
                type="button"
                onClick={() => window.location.href = 'tel:9842309599'}
                className="bg-[#F8A356] text-white px-8 py-4 rounded-lg hover:bg-[#3A9AB8] transition-colors font-semibold"
              >
                Call Instead
              </button>
            </div>
          </form>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-[#4AB8D8]/10 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-3 text-[#3A9AB8]">What Happens Next?</h3>
          <ol className="space-y-2 text-gray-700 list-decimal list-inside">
            <li>You'll receive an email confirmation of your appointment request</li>
            <li>Our staff will contact you within 24 hours to confirm your appointment</li>
            <li>You'll receive appointment reminders via email and SMS</li>
            <li>Bring valid ID and insurance card (if applicable) to your appointment</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
